import { useEffect, useRef, useState } from 'react'
import { HStack, Button } from '@chakra-ui/react';
import { FiEdit3, FiTrash2 } from 'react-icons/fi'
import type { Map as MapLibreMap } from 'maplibre-gl'
import { TerraDraw, TerraDrawPolygonMode, TerraDrawSelectMode } from 'terra-draw'
import { TerraDrawMapLibreGLAdapter } from 'terra-draw-maplibre-gl-adapter'
import type * as GeoJSON from 'geojson'
import { TurfMap } from './TurfMap'
import type { TurfDrawingMapProps } from '../../types/turfMap'

export function TurfDrawingMap({
  onPolygonComplete,
  onPolygonClear,
  drawingEnabled = true,
  ...mapProps
}: TurfDrawingMapProps) {
  const drawRef = useRef<TerraDraw | null>(null)
  const mapRef = useRef<MapLibreMap | null>(null)
  const [isDrawing, setIsDrawing] = useState(false)

  // Handle map load - just store the map reference
  const handleMapLoad = (map: MapLibreMap) => {
    console.log('[TurfDrawingMap] Map loaded:', map)
    mapRef.current = map
    mapProps.onMapLoad?.(map)
  }

  // Initialize terra-draw when drawingEnabled becomes true
  useEffect(() => {
    if (!drawingEnabled || !mapRef.current || drawRef.current) {
      return
    }

    const map = mapRef.current
    console.log('[TurfDrawingMap] Initializing terra-draw with map:', map)

    // Create terra-draw instance
    const draw = new TerraDraw({
      adapter: new TerraDrawMapLibreGLAdapter({ map }),
      modes: [
        new TerraDrawPolygonMode({
          styles: {
            fillColor: '#3182ce',
            fillOpacity: 0.2,
            outlineColor: '#3182ce',
            outlineWidth: 2,
          },
        }),
        new TerraDrawSelectMode({
          flags: {
            polygon: {
              feature: {
                draggable: true,
                coordinates: {
                  midpoints: true,
                  draggable: true,
                  deletable: true,
                },
              },
            },
          },
        }),
      ],
    })

    console.log('[TurfDrawingMap] Terra-draw instance created:', draw)

    // Listen for drawing completion
    draw.on('finish', (id: string | number) => {
      const snapshot = draw.getSnapshot()
      const feature = snapshot.find(f => f.id === id)

      if (feature && feature.geometry.type === 'Polygon') {
        // Convert terra-draw feature to GeoJSON
        const geoJsonFeature: GeoJSON.Feature<GeoJSON.Polygon> = {
          type: 'Feature',
          geometry: feature.geometry as GeoJSON.Polygon,
          properties: feature.properties || {},
        }

        onPolygonComplete(geoJsonFeature)
        // Switch back to select mode after drawing
        draw.setMode('select')
        setIsDrawing(false)
      }
    })

    // Start terra-draw in select mode (static, non-interactive)
    draw.start()
    console.log('[TurfDrawingMap] Terra-draw started')
    draw.setMode('select')
    console.log('[TurfDrawingMap] Mode set to: select')
    drawRef.current = draw
  }, [drawingEnabled, onPolygonComplete])

  // Cleanup on unmount or when drawingEnabled becomes false
  useEffect(() => {
    return () => {
      if (drawRef.current) {
        console.log('[TurfDrawingMap] Cleaning up terra-draw')
        drawRef.current.stop()
        drawRef.current = null
      }
    }
  }, [])

  // Handle draw polygon button
  const handleStartDrawing = () => {
    if (!drawRef.current) {
      console.error('[TurfDrawingMap] Draw ref is null, cannot start drawing')
      return
    }

    console.log('[TurfDrawingMap] Setting mode to polygon')
    drawRef.current.setMode('polygon')
    console.log('[TurfDrawingMap] Current mode:', drawRef.current.getMode())
    setIsDrawing(true)
  }

  // Handle clear button
  const handleClear = () => {
    if (!drawRef.current) return

    drawRef.current.clear()
    drawRef.current.setMode('select')
    setIsDrawing(false)
    onPolygonClear?.()
  }

  return (
    <div style={{ position: 'relative' }}>
      <TurfMap {...mapProps} onMapLoad={handleMapLoad} disableInteractions={isDrawing} />

      {/* Drawing Toolbar */}
      {drawingEnabled && (
        <HStack
          position="absolute"
          top={4}
          right={4}
          gap={2}
          bg="surface"
          p={2}
          borderRadius="md"
          shadow="md"
        >
          <Button
            size="sm"
            colorPalette="brand"
            variant={isDrawing ? 'solid' : 'outline'}
            onClick={handleStartDrawing}><FiEdit3 />Draw Polygon
                      </Button>
          <Button size="sm" colorPalette="red" variant="ghost" onClick={handleClear}><FiTrash2 />Clear
                      </Button>
        </HStack>
      )}
    </div>
  );
}
